import React, { useEffect, useRef, useState } from 'react';
import FadeIn from 'react-fade-in/lib/FadeIn';
import { LockClosedIcon, SearchCircleIcon, MailIcon } from '@heroicons/react/solid';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Landingfooter from './Landingfooter';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import blockTools, { normalizeBlock } from '@sanity/block-tools';
import Schema from '@sanity/schema';
import Signin from './Signin';
import Toast from './Toast';
import LoadingOverlay from './LoadingOverlay';

function CreateProfile() {
  const { currentUser, signout, verifyEmail } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [web, setWeb] = useState('');
  const [phone, setPhone] = useState('');
  const [bio, setBio] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('Unable to sign out. Please try again.');

  // Push Block contents
  const defaultSchema = Schema.compile({
    name: 'myBlog',
    types: [
      {
        type: 'object',
        name: 'blogPost',
        fields: [
          {
            title: 'Title',
            type: 'string',
            name: 'title',
          },
          {
            title: 'Body',
            name: 'body',
            type: 'array',
            of: [{ type: 'block' }],
          },
        ],
      },
    ],
  });

  const blockContentType = defaultSchema
    .get('blogPost')
    .fields.find((field) => field.name === 'body').type;

  //   Handle Signout
  function handleSignout(e) {
    try {
      signout();
      navigate('/');
    } catch {
      setErrorMessage('Unable to sign out. Please try again.');
      setShowErrorModal(true);
    }
  }

  //   Handle Submit
  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);

    const blocks = blockTools.htmlToBlocks(bio, blockContentType);
    const readyBlocks = normalizeBlock(blocks, {
      allowedDecorators: ['strong', 'em', 'underline'],
    });

    const employer = {
      _id: currentUser.uid,
      _type: 'employer',
      email: currentUser.email,
      name: name,
      website: web || 'N/A',
      contact: phone || 'N/A',
      fullAddress: address,
      bio: readyBlocks,
      postCredits: 0,
      legacyBalance: 0,
      promoCode: promoCode.trim(),
      referralCode: referralCode.trim(),
    };

    try {
      await axios.post('/.netlify/functions/createEmployer', employer);
      verifyEmail();
      setShowVerificationModal(true);
      setLoading(false);
    } catch (err) {
      console.error(err);
      const serverMessage = err && err.response && err.response.status === 400 && err.response.data && err.response.data.error;
      setErrorMessage(serverMessage || 'Unable to create your profile. Please try again.');
      setShowErrorModal(true);
      setLoading(false);
    }
  }

  if (loading) {
    return <LoadingOverlay />;
  }

  if (!currentUser) {
    return <Navigate to="/sign-in" replace />;
  } else {
    return (
      <>
        <FadeIn>
          <div className="sm:px-4 sm:mx-4 xl:px-32 xl:mx-32 py-2 sm:py-16">
            <div className="mt-10 sm:mt-0">
              <div className="xl:grid xl:grid-cols-3 xl:gap-6">
                <div className="md:col-span-1">
                  <div className="px-4 sm:px-0">
                    <h3 className="text-lg text-center xl:text-left font-bold text-primary leading-6">
                      Create Your Employer Profile
                    </h3>
                    <p className="mt-1 text-center xl:text-left text-sm text-gray-900">
                      Complete your profile to start posting jobs across multiple specialized job boards.
                    </p>
                    <button
                      onClick={handleSignout}
                      type="button"
                      className="mt-4 w-full sm:w-auto px-4 py-2 border border-primary text-sm font-medium rounded-md text-primary bg-white hover:bg-red-50"
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
                <div className="mt-5 md:mt-0 md:col-span-2">
                  <form onSubmit={handleSubmit}>
                    <div className="shadow overflow-hidden sm:rounded-md">
                      <div className="px-4 py-5 bg-white sm:p-6">
                        <div className="grid grid-cols-6 gap-6">
                          {/* Company Name */}
                          <div className="col-span-6">
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                              Company Name
                            </label>
                            <input
                              required
                              type="text"
                              name="name"
                              id="name"
                              value={name}
                              onInput={(e) => setName(e.target.value)}
                              className="mt-1 focus:ring-red-500 focus:border-red-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            />
                          </div>

                          {/* Company Bio */}
                          <div className="col-span-6">
                            <label htmlFor="bio" className="block text-sm font-medium text-gray-700">
                              Company Profile
                            </label>
                            <div className="mt-1">
                              <ReactQuill
                                value={bio}
                                onChange={setBio}
                                theme="snow"
                                placeholder="Tell job seekers about your company, mission, and culture..."
                              />
                            </div>
                            <p className="mt-2 text-sm text-gray-500">
                              Brief description of your company.
                            </p>
                          </div>

                          {/* Website */}
                          <div className="col-span-6 sm:col-span-3">
                            <label htmlFor="web" className="block text-sm font-medium text-gray-700">
                              Website <span className="text-gray-500">(optional)</span>
                            </label>
                            <input
                              type="text"
                              name="web"
                              id="web"
                              value={web}
                              onInput={(e) => setWeb(e.target.value)}
                              placeholder="yourcompany.com"
                              className="mt-1 focus:ring-red-500 focus:border-red-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            />
                          </div>

                          {/* Phone */}
                          <div className="col-span-6 sm:col-span-3">
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                              Contact Phone <span className="text-gray-500">(optional)</span>
                            </label>
                            <input
                              type="tel"
                              name="phone"
                              id="phone"
                              value={phone}
                              onInput={(e) => setPhone(e.target.value)}
                              placeholder="(123) 456-7890"
                              className="mt-1 focus:ring-red-500 focus:border-red-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            />
                          </div>

                          {/* Address */}
                          <div className="col-span-6">
                            <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                              Company Address
                            </label>
                            <input
                              required
                              type="text"
                              name="address"
                              id="address"
                              value={address}
                              onInput={(e) => setAddress(e.target.value)}
                              placeholder="123 Main St, City, Province, Postal Code"
                              className="mt-1 focus:ring-red-500 focus:border-red-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            />
                          </div>

                          {/* Promo / referral codes (GROWTH-001/002) */}
                          <div className="col-span-6 sm:col-span-3">
                            <label htmlFor="promoCode" className="block text-sm font-medium text-gray-700">
                              Promo code <span className="text-gray-500">(optional)</span>
                            </label>
                            <input
                              type="text"
                              name="promoCode"
                              id="promoCode"
                              value={promoCode}
                              onInput={(e) => setPromoCode(e.target.value)}
                              maxLength={32}
                              autoComplete="off"
                              className="mt-1 uppercase focus:ring-red-500 focus:border-red-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            />
                            <p className="mt-2 text-sm text-gray-500">Credit is added once you verify your email.</p>
                          </div>
                          <div className="col-span-6 sm:col-span-3">
                            <label htmlFor="referralCode" className="block text-sm font-medium text-gray-700">
                              Referral code <span className="text-gray-500">(optional)</span>
                            </label>
                            <input
                              type="text"
                              name="referralCode"
                              id="referralCode"
                              value={referralCode}
                              onInput={(e) => setReferralCode(e.target.value)}
                              maxLength={32}
                              autoComplete="off"
                              className="mt-1 uppercase focus:ring-red-500 focus:border-red-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            />
                            <p className="mt-2 text-sm text-gray-500">You both get $10 credit after your first paid posting.</p>
                          </div>
                        </div>
                      </div>
                      <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                        <button
                          type="submit"
                          disabled={loading}
                          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                        >
                          Create Profile & Verify Email
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </FadeIn>

        {/* Email Verification Modal */}
        {showVerificationModal && (
          <div className="fixed z-10 inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowVerificationModal(false)}></div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <MailIcon className="h-6 w-6 text-primary" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Verify Your Email
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          A verification email has been sent to your email account. Follow the link provided in the email to verify your account and continue. Thank you!
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Go to Dashboard
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <Toast
          isOpen={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          message={errorMessage}
          type="error"
        />

        <Landingfooter />
      </>
    );
  }
}

export default CreateProfile;
